import { MetricValueMode } from '@statsbomb/parachute-types';
import { RadarDataFormat } from '@/types/radar';
import { getMetricBaseName } from '@/utils/metrics';
import { trackContentFilterEvent, trackObjectConfigurationEvent } from './analytics';
import {
  ObjectConfigurationConfigurationType,
  ObjectConfigurationObjectSubType,
  ObjectConfigurationObjectType,
} from './types';

export const metricValueModeTrackingMap = {
  [MetricValueMode.Per90]: 'per_90',
  [MetricValueMode.Total]: 'totals',
} as const;

type MetricDisplayFormat = (typeof metricValueModeTrackingMap)[keyof typeof metricValueModeTrackingMap];

export const trackFilterChange = (
  filterType: string,
  appliedVsRemoved: 'Applied' | 'Removed',
  filterSource: string,
  filterSelection: string,
) =>
  trackContentFilterEvent({
    filterType,
    filterSource,
    appliedVsRemoved,
    fromSavedFilters: false,
    filterSelection,
  });

export const batchTrackMetricFilters = async (metrics: string[], fromSavedFilters: boolean) => {
  const trackingEvents = metrics.map(metric =>
    trackContentFilterEvent({
      filterType: getMetricBaseName(metric),
      filterSource: 'player scout',
      appliedVsRemoved: 'Applied',
      fromSavedFilters,
    }),
  );

  return Promise.all(trackingEvents);
};

export const trackMetricDisplayFormatEvent = async ({
  previousConfiguration,
  updatedConfiguration,
}: {
  previousConfiguration: MetricDisplayFormat;
  updatedConfiguration: MetricDisplayFormat;
}) =>
  trackObjectConfigurationEvent({
    objectType: ObjectConfigurationObjectType.Visualisation,
    objectSubType: ObjectConfigurationObjectSubType.Table,
    configurationType: ObjectConfigurationConfigurationType.MetricDisplayFormat,
    previousConfiguration,
    updatedConfiguration,
  });

export const trackRadarDataFormatEvent = async ({
  previousConfiguration,
  updatedConfiguration,
}: {
  previousConfiguration: RadarDataFormat;
  updatedConfiguration: RadarDataFormat;
}) =>
  trackObjectConfigurationEvent({
    objectType: ObjectConfigurationObjectType.Visualisation,
    objectSubType: ObjectConfigurationObjectSubType.Radar,
    configurationType: ObjectConfigurationConfigurationType.DataFormat,
    previousConfiguration,
    updatedConfiguration,
  });

type SecondaryDataFormat = 'none' | 'rank';
export const toSecondaryDataFormat = (showRank: boolean): SecondaryDataFormat => (showRank ? 'rank' : 'none');

export const trackSecondaryDataFormatEvent = ({
  previousConfiguration,
  updatedConfiguration,
}: {
  previousConfiguration: SecondaryDataFormat;
  updatedConfiguration: SecondaryDataFormat;
}) =>
  trackObjectConfigurationEvent({
    objectType: ObjectConfigurationObjectType.Visualisation,
    objectSubType: ObjectConfigurationObjectSubType.Table,
    configurationType: ObjectConfigurationConfigurationType.SecondaryDataFormat,
    previousConfiguration,
    updatedConfiguration,
  });
